/** ドキュメントアプリ V4 — 表示用の共通ラベル・バッジ（専門用語を使わない日本語）。 */
import {
  FilePdf, FileImage, FileText, FileZip, Globe, UsersThree, User, LockKey,
} from '@phosphor-icons/react';

export const fileIcon = (doc, size = 24) => {
  const m = doc?.mime_type || '';
  if (m === 'application/pdf') return <FilePdf size={size} weight="duotone" className="text-rose-500" />;
  if (m.startsWith('image/')) return <FileImage size={size} weight="duotone" className="text-sky-500" />;
  if (m.includes('zip')) return <FileZip size={size} weight="duotone" className="text-amber-500" />;
  return <FileText size={size} weight="duotone" className="text-zinc-400" />;
};

export const hasThumb = (doc) => {
  const m = (doc?.mime_type || '').toLowerCase();
  return doc?.preview_allowed !== false && (m === 'application/pdf' || m.startsWith('image/'));
};

/** 進行中の状態 — 「OCR」などの専門用語は使わない */
export const STAGE_VIEW = {
  uploading: { label: 'アップロード中', cls: 'bg-zinc-100 text-zinc-600 border-zinc-200', busy: true },
  uploaded: { label: '順番待ち', cls: 'bg-zinc-100 text-zinc-600 border-zinc-200', busy: true },
  queued: { label: '順番待ち', cls: 'bg-zinc-100 text-zinc-600 border-zinc-200', busy: true },
  ocr: { label: '文字を読み取り中', cls: 'bg-sky-50 text-sky-700 border-sky-200', busy: true },
  analyzing: { label: 'AIが内容を読み取り中', cls: 'bg-violet-50 text-violet-700 border-violet-200', busy: true },
  review: { label: '確認待ち', cls: 'bg-amber-50 text-amber-800 border-amber-200', busy: false },
  ready: { label: '保存済み', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', busy: false },
  failed: { label: '取り込み失敗', cls: 'bg-rose-50 text-rose-700 border-rose-200', busy: false },
  duplicate: { label: '重複のため取り込みません', cls: 'bg-orange-50 text-orange-800 border-orange-200', busy: false },
};

/** 信号機表示（％は出さない） */
export const BAND_VIEW = {
  auto: { label: 'そのまま保存できます', dot: 'bg-emerald-500', text: 'text-emerald-700', box: 'border-emerald-200 bg-emerald-50' },
  recommended: { label: '確認をおすすめします', dot: 'bg-amber-500', text: 'text-amber-800', box: 'border-amber-200 bg-amber-50' },
  required: { label: '確認が必要です', dot: 'bg-rose-500', text: 'text-rose-700', box: 'border-rose-200 bg-rose-50' },
};

export const bandOf = (item) => {
  if (item?.error) return 'required';
  return item?.band || (item?.confidence >= 90 ? 'auto' : item?.confidence >= 70 ? 'recommended' : 'required');
};

export const TrafficDot = ({ band, className = '' }) => (
  <span className={`inline-block h-2 w-2 rounded-full ${BAND_VIEW[band]?.dot || 'bg-zinc-300'} ${className}`} />
);

/** 公開範囲バッジ — 既存のフォルダー権限（view_scope）と機密区分から表示だけを生成する */
export const folderAccess = (folder) => {
  const scope = folder?.view_scope || 'engineering';
  if (scope === 'all') return { label: '全社公開', Icon: Globe, cls: 'text-zinc-500' };
  if (scope === 'custom') return { label: '指定メンバー', Icon: User, cls: 'text-amber-700' };
  return { label: '技術メンバー', Icon: UsersThree, cls: 'text-zinc-500' };
};

export const AccessBadge = ({ folder, size = 12 }) => {
  const a = folderAccess(folder);
  return (
    <span className={`inline-flex items-center gap-1 text-xs ${a.cls}`} data-testid="dms-folder-access">
      <a.Icon size={size} weight="duotone" /> {a.label}
    </span>
  );
};

export const ConfBadge = ({ doc, size = 12 }) => {
  if (!['restricted', 'confidential'].includes(doc?.confidentiality)) return null;
  return (
    <span className="inline-flex items-center gap-1 rounded-md border border-rose-200 bg-rose-50 px-1.5 py-0.5 text-xs text-rose-700"
      data-testid={`dms-conf-badge-${doc.id}`}>
      <LockKey size={size} weight="fill" /> 機密
    </span>
  );
};

export const folderPath = (folders, folderId) => {
  const by = new Map((folders || []).map((f) => [f.id, f]));
  const out = [];
  let cur = by.get(folderId);
  let guard = 0;
  while (cur && guard < 12) {
    out.unshift(cur);
    cur = cur.parent_folder_id ? by.get(cur.parent_folder_id) : null;
    guard += 1;
  }
  return out;
};
